import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Sheet, SheetContent } from '../../../components/ui/sheet';
import { useTranslation } from 'react-i18next';
import { UploadDropzone } from '../Shared/UploadDropzone';
import { DocumentsList } from '../Shared/DocumentsList';
import { ExtractionResults } from '../Shared/ExtractionResults';
import { ROLES } from '../../../utils/rbac';
import { EXTRACTION_STATUS, generateMockExtractedData } from '../../../utils/mockDocumentsData';
import { useToast } from '../../../hooks/use-toast';
import './DocumentsDrawer.css';

export function DocumentsDrawer({ 
  isOpen, 
  onClose, 
  documents = [], 
  onUpload,
  currentUser,
  activeStep = null 
}) {
  const [localDocuments, setLocalDocuments] = useState(documents);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const { toast } = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setLocalDocuments(documents);
  }, [documents]);

  const canUpload = currentUser?.role === ROLES.ADMIN || 
                    currentUser?.role === ROLES.SUPERADMIN || 
                    currentUser?.role === ROLES.REVIEWER;

  const handleFileUpload = async (files) => {
    const uploadedFiles = Array.from(files);
    
    for (const file of uploadedFiles) {
      // Validate file
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: t('reports.workspace.documents.toast.fileTooLarge.title'),
          description: t('reports.workspace.documents.toast.fileTooLarge.description', {
            name: file.name,
            max: '10MB',
          }),
          variant: 'destructive',
        });
        continue;
      }
      
      // Create document object
      const document = {
        id: `doc-${Date.now()}-${Math.random()}`,
        name: file.name,
        type: file.type || file.name.split('.').pop().toLowerCase(),
        size: file.size,
        uploadedAt: new Date(),
        uploadedBy: currentUser?.email || 'User',
        extractionStatus: EXTRACTION_STATUS.PENDING,
        fileUrl: URL.createObjectURL(file),
      };
      
      // Add to documents list
      setLocalDocuments(prev => [...prev, document]);
      
      // Call parent onUpload if provided
      if (onUpload) {
        onUpload(document);
      }
      
      // Simulate extraction
      simulateExtractionForDocument(document.id);
    }
  };

  const simulateExtractionForDocument = async (docId) => {
    // Update status to processing
    setLocalDocuments(prev =>
      prev.map(doc =>
        doc.id === docId
          ? { ...doc, extractionStatus: EXTRACTION_STATUS.PROCESSING }
          : doc
      )
    );
    
    // Simulate extraction delay
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Update status to complete
    setLocalDocuments(prev =>
      prev.map(doc =>
        doc.id === docId
          ? { ...doc, extractionStatus: EXTRACTION_STATUS.COMPLETE, extractionResults: generateMockExtractedData() }
          : doc
      )
    );
  };

  const handleView = (document) => {
    // In production, this would open a document preview modal
    console.log('View document:', document.id);
    toast({
      title: t('reports.workspace.documents.toast.preview.title'),
      description: t('reports.workspace.documents.toast.preview.description', { name: document.name }),
    });
  };

  const handleDownload = (document) => {
    // In production, this would download the document
    console.log('Download document:', document.id);
    toast({
      title: t('reports.workspace.documents.toast.download.title'),
      description: t('reports.workspace.documents.toast.download.description', { name: document.name }),
    });
  };

  const handleDelete = (document) => {
    setLocalDocuments(prev => prev.filter(doc => doc.id !== document.id));
    // In production, this would delete from server
    console.log('Delete document:', document.id);
  };

  const handleReExtract = async (document) => {
    // Update to processing
    setLocalDocuments(prev =>
      prev.map(doc =>
        doc.id === document.id
          ? { ...doc, extractionStatus: EXTRACTION_STATUS.PROCESSING }
          : doc
      )
    );

    // Simulate re-extraction
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setLocalDocuments(prev =>
      prev.map(doc =>
        doc.id === document.id
          ? { ...doc, extractionStatus: EXTRACTION_STATUS.COMPLETE, extractionResults: generateMockExtractedData() }
          : doc
      )
    );
  };

  const handleUseExtractedData = (documentsWithResults) => {
    // In production, this would navigate to relevant modules or show a modal
    console.log('Use extracted data from:', documentsWithResults);
    toast({
      title: t('reports.workspace.documents.toast.useExtracted.title'),
      description: t('reports.workspace.documents.toast.useExtracted.description'),
    });
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side={isMobile ? "bottom" : "right"} className="documents-drawer">
        <div className="documents-drawer__header">
          <h2 className="documents-drawer__title">{t('reports.workspace.documentsDrawer.title')}</h2>
          <button 
            className="documents-drawer__close-btn"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <X size={20} />
          </button>
        </div>

        <div className="documents-drawer__content">
          {/* Upload Section */}
          {canUpload && (
            <div className="documents-drawer__upload-section">
              <h3 className="documents-drawer__section-title">{t('reports.workspace.documentsDrawer.uploadTitle')}</h3>
              <UploadDropzone
                onUpload={handleFileUpload}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                maxFiles={10}
              />
            </div>
          )}

          {/* Documents List Section */}
          <div className="documents-drawer__list-section">
            <h3 className="documents-drawer__section-title">{t('reports.workspace.documentsDrawer.uploadedTitle')}</h3>
            <DocumentsList
              documents={localDocuments}
              currentUser={currentUser}
              onView={handleView}
              onDownload={handleDownload}
              onDelete={handleDelete}
              onReExtract={handleReExtract}
            />
          </div>

          {/* Extraction Results Section */}
          {localDocuments.some(doc => doc.extractionStatus === EXTRACTION_STATUS.COMPLETE && doc.extractionResults) && (
            <div className="documents-drawer__results-section">
              <h3 className="documents-drawer__section-title">{t('reports.workspace.documentsDrawer.resultsTitle')}</h3>
              <ExtractionResults
                documents={localDocuments}
                onReExtract={handleReExtract}
                onUseExtractedData={handleUseExtractedData}
              />
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
